import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Images, Loader2, Plus, Ruler, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@groot/ui/button";
import { Checkbox } from "@groot/ui/checkbox";
import { EmptyState } from "@groot/ui/empty-state";
import { SkeletonTable } from "@groot/ui/loading-skeleton";
import { StatusBadge } from "@groot/ui/status-badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { Text } from "@groot/ui/typography";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@groot/ui/dialog";
import { PageLayout } from "@groot/shell/components/layout/PageLayout";
import { RowActionItem, RowActionSeparator, RowActions } from "@/components/row-actions";
import { usePhotoRequests } from "@/pages/photo-request/hooks/usePhotoRequests";
import {
  useCreatePrintBatch,
  useDeletePrintBatch,
  usePrintBatches,
  type PrintBatchListItem,
  type PrintBatchSourceSummary,
} from "./hooks/usePrintBatches";

const STATUS: Record<string, { variant: "pending" | "success" | "cancelled"; label: string }> = {
  draft: { variant: "pending", label: "draft" },
  printed: { variant: "success", label: "printed" },
  photos_cleared: { variant: "cancelled", label: "photos cleared" },
};

/** "Not shared" · "Awaiting" · "18 ♥ · 2 ✎" · "Approved" for one customer. */
export function previewStatusLabel(source: PrintBatchSourceSummary | null): string {
  if (!source?.preview) return "Not shared";
  const { loved, changes, pending, total } = source.preview;
  if (changes > 0) return `${changes} change${changes === 1 ? "" : "s"} requested`;
  if (pending === total) return "Awaiting customer";
  if (pending > 0) return `${loved} ♥ · ${pending} to review`;
  return "Approved ♥";
}

export function CreatePrintBatchDialog({
  open,
  onOpenChange,
  initialSelection = [],
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSelection?: number[];
}) {
  const navigate = useNavigate();
  const { data: requests, isLoading } = usePhotoRequests();
  const create = useCreatePrintBatch();
  const [selected, setSelected] = useState<number[]>(initialSelection);
  const candidates = useMemo(
    () =>
      (requests ?? []).filter(
        (request) => !request.photosClearedAt && (request._count?.files ?? 0) > 0,
      ),
    [requests],
  );

  const toggle = (id: number) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );

  const submit = () =>
    create.mutate(
      { photoRequestIds: selected },
      {
        onSuccess: (batch) => {
          onOpenChange(false);
          navigate(`/print-batches/${batch.id}`);
        },
        onError: (error) =>
          toast.error("Could not create the batch", { description: error.message }),
      },
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New print batch</DialogTitle>
          <DialogDescription>
            Pick the photo requests to print. Every photo is laid out at the size the customer
            ordered — pick several to fill sheets with more than one order.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-80 space-y-1 overflow-y-auto">
          {isLoading && <Text variant="meta">Loading requests…</Text>}
          {!isLoading && candidates.length === 0 && (
            <Text variant="meta">No photo requests with photos yet.</Text>
          )}
          {candidates.map((request) => (
            <label
              key={request.id}
              className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2 hover:bg-muted"
            >
              <Checkbox
                checked={selected.includes(request.id)}
                onCheckedChange={() => toggle(request.id)}
              />
              <span className="min-w-0 flex-1">
                <Text variant="body" className="block truncate font-medium">
                  {request.customer?.name}
                </Text>
                <Text variant="meta" className="block">
                  {request.order?.orderNumber} · {request._count?.files ?? 0} photos ·{" "}
                  {request.status}
                </Text>
              </span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={selected.length === 0 || create.isPending}>
            {create.isPending && <Loader2 className="animate-spin" />}
            {create.isPending
              ? "Laying out photos…"
              : `Create & open${selected.length ? ` (${selected.length})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PrintBatches() {
  const { data: batches, isLoading } = usePrintBatches();
  const deleteBatch = useDeletePrintBatch();
  const [open, setOpen] = useState(false);

  const remove = (batch: PrintBatchListItem) => {
    if (!window.confirm(`Delete “${batch.name}”? Its PDF and customer previews are removed too.`))
      return;
    deleteBatch.mutate(batch.id, {
      onSuccess: () => toast.success("Batch deleted"),
      onError: () => toast.error("Could not delete the batch"),
    });
  };

  const actions = (
    <div className="flex gap-2">
      <Button
        asChild
        variant="outline"
        title="A test sheet to check printer scaling and the cut sizes"
      >
        <a href="/api/v1/print-batches/calibration.pdf">
          <Ruler />
          Calibration sheet
        </a>
      </Button>
      <Button onClick={() => setOpen(true)}>
        <Plus />
        New batch
      </Button>
    </div>
  );

  const rowActions = (batch: PrintBatchListItem) => (
    <RowActions>
      <RowActionItem asChild>
        <Link to={`/print-batches/${batch.id}`}>
          <Eye className="size-4" />
          Open
        </Link>
      </RowActionItem>
      <RowActionSeparator />
      <RowActionItem destructive onClick={() => remove(batch)}>
        <Trash2 className="size-4" />
        Delete
      </RowActionItem>
    </RowActions>
  );

  const customers = (batch: PrintBatchListItem) =>
    batch.sources
      .filter((source): source is PrintBatchSourceSummary => Boolean(source))
      .map((source) => (
        <div key={source.photoRequestId} className="text-sm">
          <span className="text-foreground">{source.customerName}</span>{" "}
          <span className="text-muted-foreground">
            {source.orderNumber} · {previewStatusLabel(source)}
          </span>
        </div>
      ));

  return (
    <PageLayout
      title="Print"
      description="Lay out polaroid sheets, share a preview with customers, and download print-ready PDFs"
      actions={actions}
    >
      <div className="space-y-4">
        {isLoading && (
          <div className="overflow-hidden rounded-lg border">
            <SkeletonTable columns={5} rows={4} />
          </div>
        )}

        {!isLoading && batches && batches.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-border">
            <ul className="divide-y divide-border md:hidden">
              {batches.map((batch) => (
                <li key={batch.id} className="flex items-center gap-2 py-3 pr-2 pl-4">
                  <Link to={`/print-batches/${batch.id}`} className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">{batch.name}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <StatusBadge
                        status={STATUS[batch.status]!.variant}
                        label={STATUS[batch.status]!.label}
                      />
                      <span>
                        {batch._count.pages} {batch._count.pages === 1 ? "page" : "pages"} ·{" "}
                        {batch._count.photos} polaroids
                      </span>
                    </div>
                  </Link>
                  {rowActions(batch)}
                </li>
              ))}
            </ul>
            <div className="hidden overflow-x-auto md:block">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Batch</TableHead>
                    <TableHead>Customers & preview</TableHead>
                    <TableHead>Sheets</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map((batch) => (
                    <TableRow key={batch.id} className="border-border hover:bg-muted/50">
                      <TableCell className="text-sm">
                        <Link
                          to={`/print-batches/${batch.id}`}
                          className="font-medium text-foreground hover:text-primary"
                        >
                          {batch.name}
                        </Link>
                      </TableCell>
                      <TableCell>{customers(batch)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {batch._count.pages} {batch._count.pages === 1 ? "page" : "pages"} ·{" "}
                        {batch._count.photos} polaroids
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          status={STATUS[batch.status]!.variant}
                          label={STATUS[batch.status]!.label}
                        />
                      </TableCell>
                      <TableCell>{rowActions(batch)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {!isLoading && batches?.length === 0 && (
          <EmptyState
            icon={Images}
            title="No print batches yet"
            description="Create one from a photo request: every photo is laid out on A4 polaroid sheets for you."
            action={
              <Button onClick={() => setOpen(true)}>
                <Plus />
                New batch
              </Button>
            }
          />
        )}
      </div>
      {open && <CreatePrintBatchDialog open={open} onOpenChange={setOpen} />}
    </PageLayout>
  );
}
